import { useState, useEffect, useCallback } from 'react';
import { CircleAlert, RefreshCw, Loader2, Check, X } from 'lucide-react';
import { api } from '../../../api/client';
import { useCan } from '../../../lib/rbac';

interface AllBatch {
  id: number;
  seller_id: number;
  seller_name: string;
  warehouse_group_id: number;
  warehouse_group_name: string;
  status: string;
  batch_date: string;
  opened_at: string;
  item_count: number;
  closed_count: number;
  alert_count: number;
}

interface AllReadyOrder {
  order_id: number;
  seller_id: number;
  seller_nickname: string;
  ml_order_id: number;
  buyer_nickname: string;
  paid_amount: number;
  title: string;
  thumbnail: string | null;
  has_stock: boolean;
}

interface AllGuardItem {
  wh_item_id: number;
  batch_id: number;
  ml_order_id: number;
  asin: string;
  display_title: string;
  seller_id: number;
  ship_status: string | null;
  order_status: string;
  claim_status: string | null;
}

interface AllGroup {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  sellers: { id: number; nickname: string; active: boolean }[];
}

export function AllSellersTab() {
  const can = useCan();
  const canMoney = can('global.view_money');
  const [allBatches, setAllBatches] = useState<AllBatch[]>([]);
  const [allReady, setAllReady] = useState<AllReadyOrder[]>([]);
  const [allGuards, setAllGuards] = useState<AllGuardItem[]>([]);
  const [groups, setGroups] = useState<AllGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, r, g, gr] = await Promise.all([
        api.get<AllBatch[]>('/warehouse/all-batches'),
        api.get<AllReadyOrder[]>('/warehouse/all-ready-to-ship?limit=100'),
        api.get<AllGuardItem[]>('/warehouse/all-guard-checks'),
        api.get<AllGroup[]>('/warehouse/groups'),
      ]);
      setAllBatches(Array.isArray(b.data) ? b.data : []);
      setAllReady(Array.isArray(r.data) ? r.data : []);
      setAllGuards(Array.isArray(g.data) ? g.data : []);
      setGroups(Array.isArray(gr.data) ? gr.data : []);
    } catch (err) {
      console.error('AllSellers load failed', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Agrupar batches por warehouse_group
  const batchesByGroup = allBatches.reduce<Record<number, AllBatch[]>>((acc, b) => {
    if (!acc[b.warehouse_group_id]) acc[b.warehouse_group_id] = [];
    acc[b.warehouse_group_id].push(b);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[var(--color-text-secondary)] py-8 justify-center">
        <Loader2 size={18} className="animate-spin" /> Cargando vista cross-seller…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide">
          Vista del depósito (todos los sellers)
        </h3>
        <button onClick={load} className="p-1.5 rounded-lg hover:bg-[var(--color-bg-hover)] text-[var(--color-text-secondary)]">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Bodegas (warehouse_groups) */}
      <div>
        <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] mb-2">Bodegas</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {groups.map(g => (
            <div key={g.id} className="border border-[var(--color-border)] rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold">#{g.id} · {g.name}</span>
              </div>
              {g.description && <p className="text-xs text-[var(--color-text-muted)] mb-2">{g.description}</p>}
              <div className="text-xs text-[var(--color-text-secondary)]">
                Sellers: {g.sellers.filter(s => s.active).map(s => s.nickname).join(', ') || '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Guard alerts cross-seller */}
      {allGuards.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-400 dark:border-red-600 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CircleAlert size={20} className="text-red-600 shrink-0" />
            <span className="text-sm font-bold text-red-800 dark:text-red-200">
              {allGuards.length} item(s) con problemas en batches activos (todos los sellers)
            </span>
          </div>
          <ul className="space-y-1 text-xs text-red-700 dark:text-red-300">
            {allGuards.slice(0, 10).map(g => (
              <li key={g.wh_item_id}>
                Seller {g.seller_id} · Batch #{g.batch_id} · Orden #{g.ml_order_id} ·{' '}
                {g.claim_status === 'opened' ? 'RECLAMO ABIERTO' :
                 g.ship_status === 'cancelled' ? 'ENVIO CANCELADO' :
                 g.order_status === 'cancelled' ? 'ORDEN CANCELADA' : 'PROBLEMA'}
              </li>
            ))}
            {allGuards.length > 10 && <li>… y {allGuards.length - 10} más</li>}
          </ul>
        </div>
      )}

      {/* Batches abiertos por bodega */}
      <div>
        <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
          Batches abiertos ({allBatches.length})
        </h4>
        {allBatches.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">No hay batches abiertos.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(batchesByGroup).map(([wgId, bs]) => (
              <div key={wgId} className="border border-[var(--color-border)] rounded-lg">
                <div className="px-3 py-2 bg-[var(--color-surface-alt)] border-b border-[var(--color-border)] text-xs font-semibold">
                  Bodega #{wgId} · {bs[0]?.warehouse_group_name || 'sin nombre'}
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-[var(--color-text-secondary)] bg-[var(--color-bg)]">
                    <tr>
                      <th className="text-left px-3 py-2">Seller</th>
                      <th className="text-left px-3 py-2">Batch</th>
                      <th className="text-left px-3 py-2">Items</th>
                      <th className="text-left px-3 py-2">Cerrados</th>
                      <th className="text-left px-3 py-2">Alertas</th>
                      <th className="text-left px-3 py-2">Abierto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bs.map(b => (
                      <tr key={b.id} className="border-t border-[var(--color-border)]">
                        <td className="px-3 py-2 font-medium">{b.seller_name}</td>
                        <td className="px-3 py-2">#{b.id}</td>
                        <td className="px-3 py-2">{b.item_count}</td>
                        <td className="px-3 py-2">{b.closed_count}</td>
                        <td className="px-3 py-2">{b.alert_count > 0 ? <span className="text-red-600">{b.alert_count}</span> : '—'}</td>
                        <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">
                          {new Date(b.opened_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ready to ship cross-seller */}
      <div>
        <h4 className="text-xs font-semibold text-[var(--color-text-secondary)] mb-2">
          Listas para despachar ({allReady.length})
        </h4>
        {allReady.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">No hay órdenes listas.</p>
        ) : (
          <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="text-xs text-[var(--color-text-secondary)] bg-[var(--color-surface-alt)]">
                <tr>
                  <th className="text-left px-3 py-2">Seller</th>
                  <th className="text-left px-3 py-2">Orden</th>
                  <th className="text-left px-3 py-2">Producto</th>
                  <th className="text-left px-3 py-2">Comprador</th>
                  {canMoney && <th className="text-right px-3 py-2">Monto</th>}
                  <th className="text-center px-3 py-2">Stock</th>
                </tr>
              </thead>
              <tbody>
                {allReady.slice(0, 50).map(o => (
                  <tr key={o.order_id} className="border-t border-[var(--color-border)]">
                    <td className="px-3 py-2 font-medium">{o.seller_nickname || `Seller ${o.seller_id}`}</td>
                    <td className="px-3 py-2 text-xs">#{o.ml_order_id}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[280px]" title={o.title}>{o.title}</td>
                    <td className="px-3 py-2 text-xs">{o.buyer_nickname}</td>
                    {canMoney && <td className="px-3 py-2 text-right">${o.paid_amount}</td>}
                    <td className="px-3 py-2 text-center">
                      {o.has_stock
                        ? <Check size={14} className="inline text-green-600" />
                        : <X size={14} className="inline text-red-600" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {allReady.length > 50 && (
              <div className="px-3 py-2 text-xs text-[var(--color-text-muted)] bg-[var(--color-bg)] border-t border-[var(--color-border)]">
                Mostrando primeras 50 de {allReady.length} órdenes.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
